import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WeeklyAdsAccessContext = createContext(null);

// Detect demo token from URL — persisted in sessionStorage so it survives
// intra-app navigation while a Flipp reviewer explores the demo.
const DEMO_KEY = "flipp_demo_token";
function pullDemoTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("demo_token") || params.get("token");
    if (t) sessionStorage.setItem(DEMO_KEY, t);
    return sessionStorage.getItem(DEMO_KEY) || null;
  } catch {
    return null;
  }
}

export const WeeklyAdsAccessProvider = ({ children }) => {
  const { token } = useAuth() || {};
  const [state, setState] = useState({
    loading: true,
    mode: "preview",
    allowed: false,
    reason: "denied",
    demoToken: pullDemoTokenFromUrl(),
  });

  const refresh = useCallback(async () => {
    const demoToken = pullDemoTokenFromUrl();
    try {
      const url = new URL(`${BACKEND_URL}/api/weekly-ads/access-mode`);
      if (demoToken) url.searchParams.set("demo_token", demoToken);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(url.toString(), { headers });
      const data = await res.json();
      setState({
        loading: false,
        mode: data.mode || "preview",
        allowed: !!data.allowed,
        reason: data.reason || "denied",
        demoToken,
      });
    } catch {
      setState((s) => ({ ...s, loading: false, allowed: false, reason: "denied", demoToken }));
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = {
    ...state,
    isDemo: state.reason === "demo",
    isAdmin: state.reason === "admin",
    refresh,
  };

  return (
    <WeeklyAdsAccessContext.Provider value={value}>
      {children}
    </WeeklyAdsAccessContext.Provider>
  );
};

export const useWeeklyAdsAccess = () => {
  const ctx = useContext(WeeklyAdsAccessContext);
  if (!ctx) {
    // Safe defaults if provider isn't mounted (e.g. on public pages)
    return {
      loading: true,
      mode: "preview",
      allowed: false,
      reason: "denied",
      demoToken: null,
      isDemo: false,
      isAdmin: false,
      refresh: () => {},
    };
  }
  return ctx;
};

// Helper: build a URL that includes demo_token when active (so fetches
// from downstream components inherit demo access automatically).
export const withDemoToken = (url, demoToken) => {
  if (!demoToken) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.has("demo_token")) u.searchParams.set("demo_token", demoToken);
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}demo_token=${encodeURIComponent(demoToken)}`;
  }
};
